import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types (mirror packages/admin-contracts Zod schemas)
// ---------------------------------------------------------------------------

export interface StockAdjustmentRequest {
  readonly delta: number;
  readonly reason: "restock" | "sale" | "return" | "adjustment";
  readonly notes: string | null;
  readonly expected_version: number;
}

export interface StockAdjustmentResponse {
  readonly event_id: number;
  readonly product_id: string;
  readonly previous_stock: number;
  readonly new_stock: number;
  readonly delta: number;
  readonly new_version: number;
  readonly reason: string;
  readonly notes: string | null;
  readonly created_at: string;
  readonly created_by: {
    readonly id: string;
    readonly name: string;
    readonly department: string;
  };
}

export interface StockHistoryItem {
  readonly id: number;
  readonly delta: number;
  readonly previous_stock: number;
  readonly new_stock: number;
  readonly reason: string;
  readonly notes: string | null;
  readonly created_at: string;
  readonly created_by: {
    readonly id: string;
    readonly name: string;
    readonly department: string;
  };
  readonly order_id: string | null;
}

export interface StockHistoryResponse {
  readonly items: readonly StockHistoryItem[];
  readonly next_cursor: string | null;
  readonly total: number;
}

export interface StockHistoryFilters {
  readonly cursor?: string;
  readonly limit?: number;
  readonly reason?: string;
  readonly from?: string;
  readonly to?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildHistoryParams(filters: StockHistoryFilters): string {
  const params = new URLSearchParams();
  // Backend paginates by page number; cursor is the next page as a string.
  if (filters.cursor) params.set("page", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.reason) params.set("reason", filters.reason);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// Backend envelopes responses as { ok, data, error? } or
// { ok, data, pagination }. These helpers normalize that to the shapes the
// UI already expects.
interface OkEnvelope<T> {
  readonly ok: true;
  readonly data: T;
}

interface PaginatedEnvelope<T> {
  readonly ok: true;
  readonly data: readonly T[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
    readonly pages: number;
  };
}

interface RawStockHistoryRow {
  readonly id: number;
  readonly event_data: {
    readonly delta?: number;
    readonly previous_stock?: number;
    readonly new_stock?: number;
    readonly reason?: string;
    readonly notes?: string | null;
  };
  readonly metadata: {
    readonly actor_id?: string;
    readonly actor_department?: string;
    readonly order_id?: string | null;
  };
  readonly occurred_at: string;
}

// ---------------------------------------------------------------------------
// API module
// ---------------------------------------------------------------------------

export const stockApi = {
  /** Submit a stock adjustment for a product. Uses optimistic concurrency
   * via `expected_version`; a 409 indicates a version conflict that callers
   * should resolve via a conflict-resolution dialog. */
  adjust: async (
    productId: string,
    body: StockAdjustmentRequest,
  ): Promise<StockAdjustmentResponse> => {
    const res = await adminFetch<OkEnvelope<StockAdjustmentResponse>>(
      `/api/admin/products/${productId}/stock-adjustments`,
      { method: "POST", body: JSON.stringify(body) },
    );
    return res.data;
  },

  /** Fetch paginated stock history for a product. */
  history: async (
    productId: string,
    filters: StockHistoryFilters = {},
  ): Promise<StockHistoryResponse> => {
    const res = await adminFetch<PaginatedEnvelope<RawStockHistoryRow>>(
      `/api/admin/products/${productId}/stock-adjustments${buildHistoryParams(filters)}`,
    );
    const items: StockHistoryItem[] = res.data.map((ev) => ({
      id: ev.id,
      delta: Number(ev.event_data.delta ?? 0),
      previous_stock: Number(ev.event_data.previous_stock ?? 0),
      new_stock: Number(ev.event_data.new_stock ?? 0),
      reason: String(ev.event_data.reason ?? "adjustment"),
      notes: ev.event_data.notes ?? null,
      created_at: ev.occurred_at,
      created_by: {
        id: ev.metadata.actor_id ?? "unknown",
        name: ev.metadata.actor_id ?? "unknown",
        department: ev.metadata.actor_department ?? "",
      },
      order_id: ev.metadata.order_id ?? null,
    }));
    const nextPage = res.pagination.page + 1;
    const hasMore = nextPage <= res.pagination.pages;
    return {
      items,
      next_cursor: hasMore ? String(nextPage) : null,
      total: res.pagination.total,
    };
  },
} as const;
